import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  FileSignature,
  CheckCircle,
  Briefcase,
  Scale
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import contractTypeMasterService from '../../services/contractTypeMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ContractTypeMaster = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTypes: 0,
    activeTypes: 0,
    inactiveTypes: 0,
    serviceCategory: 0,
    legalCategory: 0,
    commercialCategory: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [typesResponse, statsResponse] = await Promise.all([
        contractTypeMasterService.getAllContractTypes(),
        contractTypeMasterService.getContractTypeStats()
      ])
      
      if (typesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (typesResponse.data || []).map(type => ({
          ...type,
          id: type._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (typesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No contract type records found. Create your first contract type!')
        }
      } else {
        toast.error(typesResponse.message || 'Failed to load contract types')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading contract type master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view contract types')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load contract type master records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const getCategoryBadgeClass = (category) => {
    const categoryMap = {
      'Service': 'active',
      'Legal': 'warning',
      'Commercial': 'pending',
      'Partnership': 'active',
      'Other': 'inactive'
    }
    return categoryMap[category] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'contractTypeCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'contractTypeName',
      header: 'Contract Type Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'contractTypeDescription',
      header: 'Description',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getCategoryBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/contracts/contract-type-master/form')
  }

  const handleEdit = (typeData) => {
    navigate('/contracts/contract-type-master/edit', { state: { typeData } })
  }

  const handleView = (typeData) => {
    navigate('/contracts/contract-type-master/view', { state: { typeData } })
  }

  const handleDelete = (typeData) => {
    setDeleteItem(typeData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting contract type...')
      const response = await contractTypeMasterService.deleteContractType(deleteItem._id)
      if (response.success) {
        toast.success('Contract type deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete contract type')
      }
    } catch (error) {
      console.error('Error deleting contract type:', error)
      if (error.response?.status === 404) {
        toast.error('Contract type not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this contract type')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete contract type. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Contracts', path: '/contracts' },
          { label: 'Contract Types', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Contract Types</h1>
          <p>Define different types of contracts and agreements</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Type
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileSignature size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {loading ? '...' : (stats.totalTypes || 0)}
          </div>
          <div className="metric-title">Total Types</div>
          <div className="metric-subtitle">All contract types</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">
            {loading ? '...' : (stats.activeTypes || 0)}
          </div>
          <div className="metric-title">Active Types</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Briefcase size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Service</span>
            </div>
          </div>
          <div className="metric-value">
            {loading ? '...' : (stats.serviceCategory || 0)}
          </div>
          <div className="metric-title">Service Category</div>
          <div className="metric-subtitle">Service contracts</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Scale size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Legal</span>
            </div>
          </div>
          <div className="metric-value">
            {loading ? '...' : (stats.legalCategory || 0)}
          </div>
          <div className="metric-title">Legal Category</div>
          <div className="metric-subtitle">Legal contracts</div>
        </div>
      </div>

      {/* Data Table Section */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Contract Type"
        message={`Are you sure you want to delete contract type "${deleteItem?.contractTypeName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ContractTypeMaster

